import React, { useState, useEffect } from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  Activity,
  Users,
  Clock,
  Eye,
  FastForward,
  Bookmark,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

interface VideoAnalytics {
  lessonId: string;
  lessonTitle: string;
  totalViews: number;
  uniqueViewers: number;
  averageWatchTime: number;
  completionRate: number;
  averageEngagement: number;
  dropOffPoints: number[];
  topBookmarks: number[];
  playbackSpeedDistribution: { speed: number; count: number }[];
  engagementOverTime: { time: number; engagement: number }[];
  deviceTypes: { device: string; count: number }[];
  peakViewingHours: { hour: number; views: number }[];
}

interface VideoAnalyticsDashboardProps {
  courseId?: string;
  lessonId?: string;
  className?: string;
}

const VideoAnalyticsDashboard: React.FC<VideoAnalyticsDashboardProps> = ({
  courseId,
  lessonId,
  className = ''
}) => {
  const [analytics, setAnalytics] = useState<VideoAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedMetric, setSelectedMetric] = useState<'engagement' | 'completion' | 'dropoff'>('engagement');

  useEffect(() => {
    loadAnalytics();
  }, [courseId, lessonId, selectedPeriod]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Simular datos de analytics mientras implementamos el backend
      const mockData: VideoAnalytics[] = [
        {
          lessonId: 'lesson1',
          lessonTitle: 'Introducción al Curso',
          totalViews: 1250,
          uniqueViewers: 890,
          averageWatchTime: 420, // 7 minutos
          completionRate: 78,
          averageEngagement: 85,
          dropOffPoints: [120, 240, 360],
          topBookmarks: [45, 180, 320, 480],
          playbackSpeedDistribution: [
            { speed: 0.75, count: 45 },
            { speed: 1, count: 650 },
            { speed: 1.25, count: 180 },
            { speed: 1.5, count: 95 },
            { speed: 2, count: 30 }
          ],
          engagementOverTime: [
            { time: 0, engagement: 100 },
            { time: 60, engagement: 95 },
            { time: 120, engagement: 82 },
            { time: 180, engagement: 88 },
            { time: 240, engagement: 75 },
            { time: 300, engagement: 79 },
            { time: 360, engagement: 72 },
            { time: 420, engagement: 85 }
          ],
          deviceTypes: [
            { device: 'Desktop', count: 580 },
            { device: 'Mobile', count: 220 },
            { device: 'Tablet', count: 90 }
          ],
          peakViewingHours: [
            { hour: 9, views: 180 },
            { hour: 14, views: 250 },
            { hour: 19, views: 320 },
            { hour: 21, views: 200 }
          ]
        }
      ];
      
      setAnalytics(mockData);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

  const getEngagementColor = (engagement: number) => {
    if (engagement >= 80) return 'text-green-500';
    if (engagement >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className={`bg-card rounded-lg p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const currentAnalytics = analytics[0]; // Para demo, usar el primer elemento

  return (
    <div className={`bg-card rounded-lg p-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">
          📊 Analytics de Video
        </h2>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="bg-background border border-border rounded-lg px-3 py-1 text-sm"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
          </select>
        </div>
      </div>

      {currentAnalytics && (
        <>
          {/* Métricas principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Vistas Totales</p>
                  <p className="text-2xl font-bold text-foreground">{currentAnalytics.totalViews.toLocaleString()}</p>
                </div>
                <Eye className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Espectadores Únicos</p>
                  <p className="text-2xl font-bold text-foreground">{currentAnalytics.uniqueViewers.toLocaleString()}</p>
                </div>
                <Users className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Tiempo Promedio</p>
                  <p className="text-2xl font-bold text-foreground">{formatTime(currentAnalytics.averageWatchTime)}</p>
                </div>
                <Clock className="h-8 w-8 text-purple-500" />
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">Engagement</p>
                  <p className={`text-2xl font-bold ${getEngagementColor(currentAnalytics.averageEngagement)}`}>
                    {currentAnalytics.averageEngagement}%
                  </p>
                </div>
                <Activity className="h-8 w-8 text-orange-500" />
              </div>
            </div>
          </div>

          {/* Selector de métricas */}
          <div className="flex space-x-2 mb-4">
            {[
              { key: 'engagement', label: 'Engagement', icon: Activity },
              { key: 'completion', label: 'Finalización', icon: CheckCircle },
              { key: 'dropoff', label: 'Abandono', icon: AlertTriangle }
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setSelectedMetric(key as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedMetric === key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-muted border border-border'
                }`}
              >
                <Icon size={16} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          {/* Gráfica principal */}
          <div className="bg-background rounded-lg p-4 border border-border mb-6">
            <h3 className="text-lg font-medium text-foreground mb-4">
              Engagement a lo Largo del Video
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={currentAnalytics.engagementOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="time" 
                  tickFormatter={formatTime}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis 
                  domain={[0, 100]}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip 
                  labelFormatter={(time) => `Tiempo: ${formatTime(time as number)}`}
                  formatter={(value: number) => [`${value}%`, 'Engagement']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="engagement"
                  stroke="#06b6d4"
                  fill="url(#engagementGradient)"
                  strokeWidth={2}
                />
                <defs>
                  <linearGradient id="engagementGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribución de velocidades */}
            <div className="bg-card rounded-lg p-4 border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <FastForward className="mr-2" size={20} />
                Velocidades de Reproducción
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={currentAnalytics.playbackSpeedDistribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="speed" 
                    tickFormatter={(speed) => `${speed}x`}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" />
                  <Tooltip 
                    formatter={(value: number) => [`${value}`, 'Usuarios']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Dispositivos */}
            <div className="bg-card rounded-lg p-4 border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4">
                Tipos de Dispositivo
              </h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={currentAnalytics.deviceTypes}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                    label={({ device, percent }) => `${device} ${(percent * 100).toFixed(0)}%`}
                  >
                    {currentAnalytics.deviceTypes.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: number) => [`${value}`, 'Usuarios']}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Puntos de abandono y marcadores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-card rounded-lg p-4 border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <AlertTriangle className="mr-2 text-red-500" size={20} />
                Puntos de Abandono
              </h3>
              <div className="space-y-2">
                {currentAnalytics.dropOffPoints.map((point, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-red-500/10 rounded">
                    <span className="text-foreground">{formatTime(point)}</span>
                    <span className="text-red-500 text-sm">Alto abandono</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card rounded-lg p-4 border border-border">
              <h3 className="text-lg font-medium text-foreground mb-4 flex items-center">
                <Bookmark className="mr-2 text-yellow-500" size={20} />
                Marcadores Populares
              </h3>
              <div className="space-y-2">
                {currentAnalytics.topBookmarks.map((bookmark, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-yellow-500/10 rounded">
                    <span className="text-foreground">{formatTime(bookmark)}</span>
                    <span className="text-yellow-500 text-sm">Marcado frecuentemente</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoAnalyticsDashboard;